import { LifeRingTrigger } from 'components/ActionBar/LifeRing/LifeRingButton/LifeRingButton';
import { OpenInLifecycleButton } from 'components/secondaryMarket/productSearch/modal/buttons';
import { useOpenLifecycleFromProductId } from 'components/secondaryMarket/productSearch/utils';
import { ConnectedHelpCenterButton } from 'components/shared/HelpCenter/HelpCenterButton';
import { ListGroup, ListGroupItem } from 'components/shared/ListGroup';
import { QuoteRepriceLink } from 'components/shared/QuoteRepriceLink';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { QuoteErrorStatus } from '../status/quoteError/QuoteError';
import type { QuoteErrorSidePanelProps } from './QuoteError.connect';
import { connectQuoteErrorSidePanel } from './QuoteError.connect';

function HelpCenterButton() {
  const helpCenterEnhancement = useToggleFeature('helpCenterEnhancement');
  return helpCenterEnhancement ? (
    <div className={`d-flex w-100`}>
      <ConnectedHelpCenterButton helpCenterId={'SecondaryMarketSidePanel'} />
    </div>
  ) : (
    <LifeRingTrigger e2e="SecondaryPanelContactUs-button" className="d-flex w-100">
      <button type="button" className="btn btn-default btn-xl m-1 w-100">
        <FormattedMessage id="lifeRing.helpRequest" />
        <em className="icon icon-outlined ms-1">live_help</em>
      </button>
    </LifeRingTrigger>
  );
}
export const QuoteErrorSidePanel: FC<QuoteErrorSidePanelProps> = ({
  onGetQuote,
  isUserInternal,
  errorMessage,
  rowId,
  quoteId,
}) => {
  const { formatMessage } = useIntl();

  const message = useMemo(() => {
    const genericMessage = formatMessage({ id: 'generic.error.message' });
    return !isUserInternal ? genericMessage : (errorMessage ?? genericMessage);
  }, [formatMessage, isUserInternal, errorMessage]);

  const openLifecycleFromProductId = useOpenLifecycleFromProductId();

  return (
    <div className="d-flex flex-column h-100 overflow-y-auto">
      <QuoteErrorStatus errorMessage={message} />
      <div className="flex-grow-1">
        {quoteId && (
          <div className="h-100">
            <ListGroup>
              <ListGroupItem className="ps-0 pe-3" labelKey="blotterPanel.externalQuoteId" data-e2e="quote-id">
                <QuoteRepriceLink quoteId={quoteId} externalQuoteId={quoteId} canCopy={false} disabled />
              </ListGroupItem>
            </ListGroup>
          </div>
        )}
      </div>
      <div className="d-flex flex-shrink-0 flex-column justify-content-end">
        <HelpCenterButton />
        <div className="d-flex flex-shrink-0 flex-column">
          <button
            type="button"
            onClick={onGetQuote}
            disabled={false}
            className="btn btn-xl btn-primary m-1"
            data-e2e="btn-get-a-quote"
          >
            {formatMessage({ id: 'secondaryMarket.sidePanel.cta.getQuote' })}
          </button>
          <OpenInLifecycleButton
            className="m-1"
            labelKey="secondaryMarket.modal.buttonOpenInLifecycle"
            onClick={() => openLifecycleFromProductId(rowId)}
          />
        </div>
      </div>
    </div>
  );
};

QuoteErrorSidePanel.displayName = 'Component';

export const ConnectedQuoteErrorSidePanel = connectQuoteErrorSidePanel(QuoteErrorSidePanel);
ConnectedQuoteErrorSidePanel.displayName = 'ConnectedQuotingSidePanel';
